/**
 * Authentication Context for trAInedup.ai
 * Provides authentication state and methods throughout the application
 */

import React, { createContext, useContext, useEffect, useState, ReactNode, useCallback } from 'react';
import { User, UserRole, SubscriptionTier } from '../types';
import {
  registerWithEmail,
  loginWithEmail,
  loginWithGoogle,
  handleGoogleRedirectResult,
  logout,
  resetPassword,
  verifyResetCode,
  confirmResetPassword,
  updateUserProfile,
  updateUserEmail,
  syncUserEmail,
  reauthenticateUser,
  onAuthStateChange,
} from '../services/firebase/auth';
import { awardXP, isTaskCompleted } from '../services/firebase/gamification';
import { trackLogin } from '../services/firebase/loginStreak';
import { checkAndAwardSubscriptionXP } from '../services/firebase/gamificationHelpers';
import { auth } from '../services/firebase/config';

const ADDITIONAL_PROFILE_FIELDS = [
  'location',
  'jobTitle',
  'company',
  'website',
  'bio',
  'linkedIn',
  'linkedin',
  'twitter',
  'youtube'
] as const;

const countCompletedAdditionalProfileFields = (
  profile?: Partial<User> | Record<string, unknown> | null
): number => {
  if (!profile) {
    return 0;
  }

  return ADDITIONAL_PROFILE_FIELDS.reduce((count, field) => {
    const value = (profile as Record<string, unknown>)[field];
    if (typeof value === 'string') {
      return value.trim() ? count + 1 : count;
    }
    return value ? count + 1 : count;
  }, 0);
};

/**
 * Authentication context interface
 */
interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  loading: boolean;
  register: (userData: {
    firstName: string;
    lastName: string;
    email: string;
    password: string;
    referralCode?: string | null;
    intendedDestination?: string;
    acquisitionSource?: string;
  }) => Promise<{ success: boolean; error?: string }>;
  login: (loginData: {
    email: string;
    password: string;
  }) => Promise<{ success: boolean; error?: string; user?: User }>;
  loginWithGoogleAuth: (referralCode?: string | null) => Promise<{ success: boolean; error?: string; user?: User }>;
  logout: () => Promise<void>;
  resetUserPassword: (email: string, redirectUrl?: string) => Promise<{ success: boolean; error?: string }>;
  verifyPasswordResetCode: (oobCode: string) => Promise<{ success: boolean; email?: string; error?: string }>;
  confirmPasswordReset: (oobCode: string, newPassword: string) => Promise<{ success: boolean; error?: string }>;
  updateProfile: (userId: string, updates: any) => Promise<{ success: boolean; error?: string }>;
  updateEmailAddress: (newEmail: string) => Promise<{ success: boolean; error?: string; errorCode?: string }>;
  reauthenticate: (password: string) => Promise<{ success: boolean; error?: string }>;
  hasRole: (role: UserRole) => boolean;
  isSubscriptionActive: (tier?: SubscriptionTier) => boolean;
  isLearningPlanExpired: (user: User) => boolean;
  refreshUser: () => Promise<void>;
}

/**
 * Authentication context
 */
const AuthContext = createContext<AuthContextType | undefined>(undefined);

/**
 * Authentication provider component
 */
export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  /**
   * On mount, check for a pending Google redirect result and complete sign-in.
   */
  useEffect(() => {
    handleGoogleRedirectResult().catch((err) =>
      console.error('Error handling Google redirect result:', err)
    );
  }, []);

  /**
   * Initialize authentication state listener
   */
  useEffect(() => {
    const unsubscribe = onAuthStateChange(async (firebaseUser) => {
      if (firebaseUser) {
        // Anonymous sessions exist solely to call Cloud Functions (skills assessment).
        // The Firebase SDK retains their auth token so callable functions still work,
        // but from the app's perspective they are unauthenticated — no user object,
        // no access to protected routes, no logged-in UI state.
        if (firebaseUser.isAnonymous) {
          setUser(null);
          setIsAuthenticated(false);
          setLoading(false);
          return;
        }

        try {
          // Try to get user data from Firestore first
          const { getCurrentUserData } = await import('../services/firebase/auth');
          const firestoreResult = await getCurrentUserData(firebaseUser.uid);
          
          if (firestoreResult.success && firestoreResult.userData) {
            // Sync email/verification status if different from Auth
            if (firebaseUser.email && (firestoreResult.userData.email !== firebaseUser.email || firestoreResult.userData.emailVerified !== firebaseUser.emailVerified)) {
              syncUserEmail(firebaseUser.uid, firebaseUser.email, firebaseUser.emailVerified).catch(err => 
                console.error('Error syncing user email:', err)
              );
            }

            // Use Firestore data if available
            const userData: User = {
              uid: firebaseUser.uid,
              email: firebaseUser.email || '',
              firstName: firestoreResult.userData.firstName || '',
              lastName: firestoreResult.userData.lastName || '',
              displayName: firestoreResult.userData.displayName || firebaseUser.displayName || '',
              roles: firestoreResult.userData.roles || ['student'],
              subscriptionTier: firestoreResult.userData.subscriptionTier || SubscriptionTier.FREE,
              subscriptionStatus: firestoreResult.userData.subscriptionStatus,
              subscriptionEndDate: firestoreResult.userData.subscriptionEndDate,
              credits: firestoreResult.userData.credits,
              stripeCustomerId: firestoreResult.userData.stripeCustomerId,
              createdAt: firestoreResult.userData.createdAt?.toDate?.() || new Date(),
              updatedAt: firestoreResult.userData.updatedAt?.toDate?.() || new Date(),
              isActive: firestoreResult.userData.isActive ?? true,
              emailVerified: firestoreResult.userData.emailVerified ?? firebaseUser.emailVerified ?? false,
              profileComplete: firestoreResult.userData.profileComplete ?? false,
              bio: firestoreResult.userData.bio || '',
              location: firestoreResult.userData.location || '',
              jobTitle: firestoreResult.userData.jobTitle || '',
              company: firestoreResult.userData.company || '',
              website: firestoreResult.userData.website || '',
              linkedIn: firestoreResult.userData.linkedIn || '',
              twitter: firestoreResult.userData.twitter || '',
              intendedDestination: firestoreResult.userData.intendedDestination || undefined,
              skillsAssessmentCompleted: firestoreResult.userData.skillsAssessmentCompleted ?? false,
              skillsAssessmentCompletedAt: firestoreResult.userData.skillsAssessmentCompletedAt?.toDate?.() || undefined,
              lastAssessmentDate: firestoreResult.userData.lastAssessmentDate?.toDate?.() || undefined,
              skillsProfile: firestoreResult.userData.skillsProfile || undefined,
              activationCodeId: firestoreResult.userData.activationCodeId || undefined,
              activationCodeSource: firestoreResult.userData.activationCodeSource || undefined,
              activationCodeCampaignId: firestoreResult.userData.activationCodeCampaignId || undefined,
              activationCodeRedeemedAt: firestoreResult.userData.activationCodeRedeemedAt?.toDate?.() || undefined,
              membershipPlan: firestoreResult.userData.membershipPlan || undefined,
              acquisitionSource: firestoreResult.userData.acquisitionSource || undefined,
              currentLearningPlanId: firestoreResult.userData.currentLearningPlanId || undefined,
            };
            setUser(userData);
            
            // Track login for streak
            trackLogin(firebaseUser.uid).catch(err => 
              console.error('Error tracking login:', err)
            );
            
            // Check and award subscription XP
            const hasActiveSubscription = userData.subscriptionTier !== SubscriptionTier.FREE;
            checkAndAwardSubscriptionXP(firebaseUser.uid, hasActiveSubscription).catch(err =>
              console.error('Error checking subscription XP:', err)
            );
          } else {
            // Fallback to Firebase Auth data
            const userData: User = {
              uid: firebaseUser.uid,
              email: firebaseUser.email || '',
              firstName: firebaseUser.displayName?.split(' ')[0] || '',
              lastName: firebaseUser.displayName?.split(' ').slice(1).join(' ') || '',
              displayName: firebaseUser.displayName || '',
              roles: ['student'],
              subscriptionTier: SubscriptionTier.FREE,
              createdAt: new Date(),
              updatedAt: new Date(),
              isActive: true,
              emailVerified: firebaseUser.emailVerified,
              profileComplete: false,
              activationCodeId: undefined,
              activationCodeSource: undefined,
              activationCodeCampaignId: undefined,
              activationCodeRedeemedAt: undefined,
              membershipPlan: undefined,
              acquisitionSource: undefined,
            };
            setUser(userData);
          }
        } catch (error) {
          console.error('Error loading user data:', error);
          // Fallback to Firebase Auth data
          const userData: User = {
            uid: firebaseUser.uid,
            email: firebaseUser.email || '',
            firstName: firebaseUser.displayName?.split(' ')[0] || '',
            lastName: firebaseUser.displayName?.split(' ').slice(1).join(' ') || '',
            displayName: firebaseUser.displayName || '',
            roles: ['student'],
            subscriptionTier: SubscriptionTier.FREE,
            createdAt: new Date(),
            updatedAt: new Date(),
            isActive: true,
            emailVerified: firebaseUser.emailVerified ?? false,
            profileComplete: false,
            activationCodeId: undefined,
            activationCodeSource: undefined,
            activationCodeCampaignId: undefined,
            activationCodeRedeemedAt: undefined,
            membershipPlan: undefined,
            acquisitionSource: undefined,
          };
          setUser(userData);
        }
        setIsAuthenticated(true);
      } else {
        setUser(null);
        setIsAuthenticated(false);
      }
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  /**
   * Register new user
   */
  const register = async (userData: {
    firstName: string;
    lastName: string;
    email: string;
    password: string;
    referralCode?: string | null;
    intendedDestination?: string;
    acquisitionSource?: string;
  }): Promise<{ success: boolean; error?: string }> => {
    try {
      setLoading(true);
      const result = await registerWithEmail(
        userData.email,
        userData.password,
        userData.firstName,
        userData.lastName,
        UserRole.STUDENT,
        userData.referralCode,
        {
          ...(userData.intendedDestination ? { intendedDestination: userData.intendedDestination } : {}),
          ...(userData.acquisitionSource ? { acquisitionSource: userData.acquisitionSource } : {}),
        }
      );
      
      if (result.success && result.userData) {
        const user: User = {
          uid: result.userData.uid,
          email: result.userData.email || '',
          firstName: result.userData.firstName || '',
          lastName: result.userData.lastName || '',
          displayName: result.userData.displayName || '',
          roles: result.userData.roles || ['student'],
          subscriptionTier: (result.userData.subscriptionTier as SubscriptionTier) || SubscriptionTier.FREE,
          subscriptionStatus: result.userData.subscriptionStatus,
          subscriptionEndDate: result.userData.subscriptionEndDate,
          credits: result.userData.credits,
          stripeCustomerId: result.userData.stripeCustomerId,
          createdAt: result.userData.createdAt || new Date(),
          updatedAt: result.userData.updatedAt || new Date(),
          isActive: result.userData.isActive ?? true,
          emailVerified: result.userData.emailVerified ?? false,
          profileComplete: result.userData.profileComplete ?? false,
          intendedDestination: result.userData.intendedDestination || undefined,
          skillsAssessmentCompleted: result.userData.skillsAssessmentCompleted ?? false,
          skillsAssessmentCompletedAt: result.userData.skillsAssessmentCompletedAt?.toDate?.() || undefined,
          lastAssessmentDate: result.userData.lastAssessmentDate?.toDate?.() || undefined,
          skillsProfile: result.userData.skillsProfile || undefined,
          activationCodeId: result.userData.activationCodeId || undefined,
          activationCodeSource: result.userData.activationCodeSource || undefined,
          activationCodeCampaignId: result.userData.activationCodeCampaignId || undefined,
          activationCodeRedeemedAt: result.userData.activationCodeRedeemedAt?.toDate?.() || undefined,
          membershipPlan: result.userData.membershipPlan || undefined,
          acquisitionSource: result.userData.acquisitionSource || undefined,
          currentLearningPlanId: result.userData.currentLearningPlanId || undefined,
        };
        
        setUser(user);
        setIsAuthenticated(true);
        return { success: true };
      }
      
      return { success: false, error: result.error || 'Registration failed' };
    } catch (error) {
      console.error('Registration error:', error);
      return { success: false, error: 'Registration failed' };
    } finally {
      setLoading(false);
    }
  };
  // (Removed stray return outside function)

  /**
   * Login user with email and password
   */
  const login = async (loginData: {
    email: string;
    password: string;
  }): Promise<{ success: boolean; error?: string; user?: User }> => {
    try {
      const result = await loginWithEmail(loginData.email, loginData.password);
      
      if (result.success && result.userData) {
        const user: User = {
          uid: result.userData.uid,
          email: result.userData.email || '',
          firstName: result.userData.firstName || '',
          lastName: result.userData.lastName || '',
          displayName: result.userData.displayName || '',
          roles: result.userData.roles || ['student'],
          subscriptionTier: (result.userData.subscriptionTier as SubscriptionTier) || SubscriptionTier.FREE,
          subscriptionStatus: result.userData.subscriptionStatus,
          subscriptionEndDate: result.userData.subscriptionEndDate,
          credits: result.userData.credits,
          stripeCustomerId: result.userData.stripeCustomerId,
          createdAt: result.userData.createdAt || new Date(),
          updatedAt: result.userData.updatedAt || new Date(),
          isActive: result.userData.isActive ?? true,
          emailVerified: result.userData.emailVerified ?? false,
          profileComplete: result.userData.profileComplete ?? false,
          intendedDestination: result.userData.intendedDestination || undefined,
          skillsAssessmentCompleted: result.userData.skillsAssessmentCompleted ?? false,
          skillsAssessmentCompletedAt: result.userData.skillsAssessmentCompletedAt?.toDate?.() || undefined,
          lastAssessmentDate: result.userData.lastAssessmentDate?.toDate?.() || undefined,
          skillsProfile: result.userData.skillsProfile || undefined,
          activationCodeId: result.userData.activationCodeId || undefined,
          activationCodeSource: result.userData.activationCodeSource || undefined,
          activationCodeCampaignId: result.userData.activationCodeCampaignId || undefined,
          activationCodeRedeemedAt: result.userData.activationCodeRedeemedAt?.toDate?.() || undefined,
          membershipPlan: result.userData.membershipPlan || undefined,
          acquisitionSource: result.userData.acquisitionSource || undefined,
          currentLearningPlanId: result.userData.currentLearningPlanId || undefined,
        };
        
        setUser(user);
        setIsAuthenticated(true);
        return { success: true, user };
      }
      
      return { success: false, error: result.error || 'Login failed' };
    } catch (error) {
      console.error('Login error:', error);
      return { success: false, error: 'Login failed' };
    }
  };
  // (Removed stray return outside function)

  /**
   * Login with Google
   */
  const loginWithGoogleAuth = async (referralCode?: string | null): Promise<{ success: boolean; error?: string; user?: User }> => {
    try {
      setLoading(true);
      const result = await loginWithGoogle(referralCode);
      
      // Redirect flow — page will reload and onAuthStateChanged handles the rest
      if (result.success && (result as any).redirectPending) {
        return { success: true };
      }

      if (result.success && result.userData) {
        const user: User = {
          uid: result.userData.uid,
          email: result.userData.email || '',
          firstName: result.userData.firstName || '',
          lastName: result.userData.lastName || '',
          displayName: result.userData.displayName || '',
          roles: result.userData.roles || ['student'],
          subscriptionTier: (result.userData.subscriptionTier as SubscriptionTier) || SubscriptionTier.FREE,
          subscriptionStatus: result.userData.subscriptionStatus,
          subscriptionEndDate: result.userData.subscriptionEndDate,
          credits: result.userData.credits,
          stripeCustomerId: result.userData.stripeCustomerId,
          createdAt: result.userData.createdAt || new Date(),
          updatedAt: result.userData.updatedAt || new Date(),
          isActive: result.userData.isActive ?? true,
          emailVerified: result.userData.emailVerified ?? false,
          profileComplete: result.userData.profileComplete ?? false,
          intendedDestination: result.userData.intendedDestination || undefined,
          skillsAssessmentCompleted: result.userData.skillsAssessmentCompleted ?? false,
          skillsAssessmentCompletedAt: result.userData.skillsAssessmentCompletedAt?.toDate?.() || undefined,
          lastAssessmentDate: result.userData.lastAssessmentDate?.toDate?.() || undefined,
          skillsProfile: result.userData.skillsProfile || undefined,
          activationCodeId: result.userData.activationCodeId || undefined,
          activationCodeSource: result.userData.activationCodeSource || undefined,
          activationCodeCampaignId: result.userData.activationCodeCampaignId || undefined,
          activationCodeRedeemedAt: result.userData.activationCodeRedeemedAt?.toDate?.() || undefined,
          membershipPlan: result.userData.membershipPlan || undefined,
          acquisitionSource: result.userData.acquisitionSource || undefined,
          currentLearningPlanId: result.userData.currentLearningPlanId || undefined,
        };

        setUser(user);
        setIsAuthenticated(true);
        return { success: true, user };
      }

      return { success: false, error: result.error || 'Google login failed' };
    } catch (error) {
      console.error('Google login error:', error);
      return { success: false, error: 'Google login failed' };
    } finally {
      setLoading(false);
    }
  };

  /**
   * Logout user
   */
  const logoutUser = async (): Promise<void> => {
    try {
      await logout();
      setUser(null);
      setIsAuthenticated(false);
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  /**
   * Reset user password
   */
  const resetUserPassword = async (email: string, redirectUrl?: string): Promise<{ success: boolean; error?: string }> => {
    try {
      const result = await resetPassword(email, redirectUrl);
      return result;
    } catch (error) {
      console.error('Password reset error:', error);
      return { success: false, error: 'Password reset failed' };
    }
  };

  /**
   * Verify password reset code and get associated email
   */
  const verifyPasswordResetCode = async (
    oobCode: string
  ): Promise<{ success: boolean; email?: string; error?: string }> => {
    try {
      const result = await verifyResetCode(oobCode);
      return result;
    } catch (error) {
      console.error('Password reset code verification error:', error);
      return { success: false, error: 'Invalid or expired reset link' };
    }
  };

  /**
   * Confirm password reset with new password
   */
  const confirmPasswordReset = async (
    oobCode: string,
    newPassword: string
  ): Promise<{ success: boolean; error?: string }> => {
    try {
      const result = await confirmResetPassword(oobCode, newPassword);
      return result;
    } catch (error) {
      console.error('Password reset confirmation error:', error);
      return { success: false, error: 'Unable to reset password' };
    }
  };

  /**
   * Update user profile
   */
  const updateProfile = async (userId: string, updates: any): Promise<{ success: boolean; error?: string }> => {
    try {
      const result = await updateUserProfile(userId, updates);
      if (result.success && user) {
        // Check if profile is being completed and award XP
        const previousFieldCount = countCompletedAdditionalProfileFields(user);
        const updatedFieldCount = countCompletedAdditionalProfileFields({
          ...user,
          ...updates
        });

        const metFieldRequirement = previousFieldCount < 2 && updatedFieldCount >= 2;

        if (metFieldRequirement) {
          try {
            const alreadyCompleted = await isTaskCompleted(userId, 'complete_profile');
            if (!alreadyCompleted) {
              await awardXP(userId, 'complete_profile', 30);
              // eslint-disable-next-line no-console
              console.log('[Gamification] Awarded 30 XP for completing profile');
              // Dispatch event to refresh gamification data
              window.dispatchEvent(new CustomEvent('gamificationUpdate'));
            }
          } catch (xpError) {
            console.error('[Gamification] Error awarding profile completion XP:', xpError);
            // Don't fail the profile update if XP awarding fails
          }
        }
        // Update local state with new data
        const updatedUser = { 
          ...user, 
          ...updates,
          updatedAt: new Date()
        };
        setUser(updatedUser);
        
        // Also refresh user data from Firestore to ensure consistency
        try {
          const { getCurrentUserData } = await import('../services/firebase/auth');
          const firestoreResult = await getCurrentUserData(userId);
          
          if (firestoreResult.success && firestoreResult.userData) {
            const refreshedUser: User = {
              ...user,
              firstName: firestoreResult.userData.firstName || user.firstName,
              lastName: firestoreResult.userData.lastName || user.lastName,
              displayName: firestoreResult.userData.displayName || user.displayName,
              roles: firestoreResult.userData.roles || user.roles || ['student'],
              bio: firestoreResult.userData.bio || '',
              location: firestoreResult.userData.location || '',
              jobTitle: firestoreResult.userData.jobTitle || '',
              company: firestoreResult.userData.company || '',
              website: firestoreResult.userData.website || '',
              linkedIn: firestoreResult.userData.linkedIn || '',
              twitter: firestoreResult.userData.twitter || '',
              updatedAt: firestoreResult.userData.updatedAt?.toDate?.() || new Date(),
            };
            setUser(refreshedUser);
          }
        } catch (refreshError) {
          console.error('Error refreshing user data:', refreshError);
          // Continue with the local update even if refresh fails
        }
      }
      return result;
    } catch (error) {
      console.error('Profile update error:', error);
      return { success: false, error: 'Profile update failed' };
    }
  };

  /**
   * Update user email address
   */
  const updateEmailAddress = async (newEmail: string): Promise<{ success: boolean; error?: string; errorCode?: string }> => {
    try {
      const result = await updateUserEmail(newEmail);
      return result;
    } catch (error) {
      console.error('Email update error:', error);
      return { success: false, error: 'Failed to initiate email update' };
    }
  };

  /**
   * Re-authenticate user
   */
  const reauthenticate = async (password: string): Promise<{ success: boolean; error?: string }> => {
    try {
      const result = await reauthenticateUser(password);
      return result;
    } catch (error) {
      console.error('Re-authentication error:', error);
      return { success: false, error: 'Re-authentication failed' };
    }
  };

  /**
   * Check if user has specific role (supports both string and array roles)
   */
  const hasRole = (role: UserRole): boolean => {
    // Support both user.role (string) and user.roles (array)
    if (!user) return false;
    if (Array.isArray(user.roles)) {
      return user.roles.includes(role);
    }
    return false;
  };

  /**
   * Check if user has active subscription
   */
  const isSubscriptionActive = (tier?: SubscriptionTier): boolean => {
    if (!user) return false;
    if (!tier) return user.subscriptionTier !== SubscriptionTier.FREE;
    return user.subscriptionTier === tier;
  };

  /**
   * Check if user's learning plan is expired (older than 30 days)
   */
  const isLearningPlanExpired = (user: User): boolean => {
    if (!user.lastAssessmentDate) return true;
    
    const lastDate = user.lastAssessmentDate instanceof Date 
      ? user.lastAssessmentDate 
      : (user.lastAssessmentDate as any).toDate?.() || new Date(user.lastAssessmentDate);
      
    const now = new Date();
    const diffTime = Math.abs(now.getTime() - lastDate.getTime());
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24)); 
    
    return diffDays > 30;
  };

  const refreshUser = useCallback(async (): Promise<void> => {
    const currentAuthUser = auth.currentUser;

    if (!currentAuthUser) {
      return;
    }

    try {
      await currentAuthUser.reload();
    } catch (reloadError) {
      console.error('Error reloading auth user:', reloadError);
    }

    try {
      const { getCurrentUserData } = await import('../services/firebase/auth');
      const firestoreResult = await getCurrentUserData(currentAuthUser.uid);

      if (firestoreResult.success && firestoreResult.userData) {
        // Sync email/verification status if different from Auth
        if (currentAuthUser.email && (firestoreResult.userData.email !== currentAuthUser.email || firestoreResult.userData.emailVerified !== currentAuthUser.emailVerified)) {
          syncUserEmail(currentAuthUser.uid, currentAuthUser.email, currentAuthUser.emailVerified).catch(err => 
            console.error('Error syncing user email:', err)
          );
        }

        const firestoreUser = firestoreResult.userData as any;
        const refreshedUser: User = {
          uid: currentAuthUser.uid,
          email: currentAuthUser.email || '',
          firstName: firestoreUser.firstName || '',
          lastName: firestoreUser.lastName || '',
          displayName: firestoreUser.displayName || currentAuthUser.displayName || '',
          roles: firestoreUser.roles || ['student'],
          subscriptionTier: (firestoreUser.subscriptionTier as SubscriptionTier) || SubscriptionTier.FREE,
          subscriptionStatus: firestoreUser.subscriptionStatus,
          subscriptionEndDate: firestoreUser.subscriptionEndDate,
          credits: firestoreUser.credits,
          stripeCustomerId: firestoreUser.stripeCustomerId,
          createdAt: firestoreUser.createdAt?.toDate?.() || new Date(),
          updatedAt: firestoreUser.updatedAt?.toDate?.() || new Date(),
          isActive: firestoreUser.isActive ?? true,
          emailVerified: firestoreUser.emailVerified ?? currentAuthUser.emailVerified ?? false,
          profileComplete: firestoreUser.profileComplete ?? false,
          bio: firestoreUser.bio || '',
          location: firestoreUser.location || '',
          jobTitle: firestoreUser.jobTitle || '',
          company: firestoreUser.company || '',
          website: firestoreUser.website || '',
          linkedIn: firestoreUser.linkedIn || '',
          twitter: firestoreUser.twitter || '',
          intendedDestination: firestoreUser.intendedDestination || undefined,
          skillsAssessmentCompleted: firestoreUser.skillsAssessmentCompleted ?? false,
          skillsAssessmentCompletedAt: firestoreUser.skillsAssessmentCompletedAt?.toDate?.() || undefined,
          lastAssessmentDate: firestoreUser.lastAssessmentDate?.toDate?.() || undefined,
          skillsProfile: firestoreUser.skillsProfile || undefined,
          activationCodeId: firestoreUser.activationCodeId || undefined,
          activationCodeSource: firestoreUser.activationCodeSource || undefined,
          activationCodeCampaignId: firestoreUser.activationCodeCampaignId || undefined,
          activationCodeRedeemedAt: firestoreUser.activationCodeRedeemedAt?.toDate?.() || undefined,
          membershipPlan: firestoreUser.membershipPlan || undefined,
          acquisitionSource: firestoreUser.acquisitionSource || undefined,
          currentLearningPlanId: firestoreUser.currentLearningPlanId || undefined,
        };

        setUser(refreshedUser);
        setIsAuthenticated(true);
        return;
      }
    } catch (error) {
      console.error('Error refreshing user data:', error);
    }

    const fallbackUser: User = {
      uid: currentAuthUser.uid,
      email: currentAuthUser.email || '',
      firstName: currentAuthUser.displayName?.split(' ')[0] || '',
      lastName: currentAuthUser.displayName?.split(' ').slice(1).join(' ') || '',
      displayName: currentAuthUser.displayName || '',
      roles: ['student'],
      subscriptionTier: SubscriptionTier.FREE,
      createdAt: new Date(),
      updatedAt: new Date(),
      isActive: true,
      emailVerified: currentAuthUser.emailVerified ?? false,
      profileComplete: false,
      activationCodeId: undefined,
      activationCodeSource: undefined,
      activationCodeCampaignId: undefined,
      activationCodeRedeemedAt: undefined,
      membershipPlan: undefined,
      acquisitionSource: undefined,
    };

    setUser(fallbackUser);
    setIsAuthenticated(true);
  }, []);

  const value: AuthContextType = {
    user,
    isAuthenticated,
    loading,
    register,
    login,
    loginWithGoogleAuth,
    logout: logoutUser,
    resetUserPassword,
    verifyPasswordResetCode,
    confirmPasswordReset,
    updateProfile,
    updateEmailAddress,
    reauthenticate,
    hasRole,
    isSubscriptionActive,
    isLearningPlanExpired,
    refreshUser,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

/**
 * Hook to use authentication context
 */
export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
